import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { cn } from '@ophelos/ui/lib/utils';
import { Sparkles } from 'lucide-react';
import { getLifecycleById } from './reconciliationLifecycles';

type StatusGroupChooserCardBadgeVariant = 'warning' | 'info' | 'failure' | 'subtle';

/** Colour tints per reconciliation status — visually distinguishes each group. */
const STATUS_TINTS: Record<string, { text: string; badge: StatusGroupChooserCardBadgeVariant }> = {
  pending: {
    text: 'text-amber-700 dark:text-amber-400',
    badge: 'warning',
  },
  classified: {
    text: 'text-blue-700 dark:text-blue-400',
    badge: 'info',
  },
  partially_matched: {
    text: 'text-violet-700 dark:text-violet-400',
    badge: 'warning',
  },
  disputed: {
    text: 'text-rose-700 dark:text-rose-400',
    badge: 'failure',
  },
  pending_review: {
    text: 'text-amber-700 dark:text-amber-400',
    badge: 'warning',
  },
};

const DEFAULT_TINT = {
  text: 'text-foreground',
  badge: 'subtle',
};

/** Maps status id → a natural prompt the user would ask. */
const STATUS_PROMPTS: Record<string, (count: number) => string> = {
  pending: (n) =>
    `I have ${n} pending bank line${n === 1 ? '' : 's'} that need classification. Can you suggest classifications for them? Start with the first one.`,
  classified: (n) =>
    `I have ${n} classified bank line${n === 1 ? '' : 's'} that need matching to a case. List these lines (id, amount, counterparty) so I can pick one for you to find matches for.`,
  partially_matched: (n) =>
    `I have ${n} partially matched bank line${n === 1 ? '' : 's'}. Show me which lines are partially matched and what's still missing.`,
  disputed: (n) =>
    `I have ${n} disputed bank line${n === 1 ? '' : 's'}. Show me the disputed lines and help me understand what needs resolving.`,
  pending_review: (n) => `I have ${n} PSP line${n === 1 ? '' : 's'} pending review. Help me match them.`,
};

interface StatusGroupChooserCardProps {
  statusCounts: Record<string, number>;
  lifecycleId: string;
  onChooseGroup: (statusId: string, prompt: string) => void;
  onDismiss?: () => void;
}

export function StatusGroupChooserCard({ statusCounts, lifecycleId, onChooseGroup, onDismiss }: StatusGroupChooserCardProps) {
  const lifecycle = getLifecycleById(lifecycleId);
  if (!lifecycle) return null;

  const groups = lifecycle.steps
    .filter((step) => step.needs_manual_review && (statusCounts[step.id] ?? 0) > 0)
    .map((step) => ({
      id: step.id,
      label: step.label,
      count: statusCounts[step.id] ?? 0,
      action: lifecycle.statusActions?.[step.id] ?? '',
    }));

  if (groups.length === 0) return null;

  return (
    <Card aria-label="Choose a status group to investigate" className="py-3 bg-soft">
      <CardHeader className="flex items-center gap-2 px-3">
        <Sparkles className="size-3.5 text-brand-600 dark:text-brand-400 shrink-0" aria-hidden />
        <CardTitle>What would you like to investigate?</CardTitle>
      </CardHeader>
      <CardContent className="px-3">
        <div className="space-y-1 mb-2">
          {groups.map((group) => {
            const tint = STATUS_TINTS[group.id] ?? DEFAULT_TINT;
            const promptFn = STATUS_PROMPTS[group.id];
            const prompt = promptFn ? promptFn(group.count) : `Help me with the ${group.count} ${group.label.toLowerCase()} bank lines.`;

            return (
              <button
                key={group.id}
                type="button"
                onClick={() => onChooseGroup(group.id, prompt)}
                className={cn(
                  'w-full flex items-baseline gap-2.5 px-2.5 py-2 rounded-lg border transition-colors cursor-pointer text-left',
                  'bg-card hover:bg-soft shadow-xs'
                )}
              >
                <Badge variant={tint.badge} size="number">
                  {group.count}
                </Badge>

                <div className="flex-1 stack min-w-0">
                  <span className={cn('text-sm font-medium', tint.text)}>{group.label}</span>
                  {group.action && <span className="text-xs text-muted-foreground">{group.action}</span>}
                </div>
              </button>
            );
          })}
        </div>

        {onDismiss && (
          <Button variant="ghost" size="sm" onClick={onDismiss} className="text-xs" aria-label="Dismiss">
            Dismiss
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
